import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Clock } from "lucide-react";
import { fetchAccessRequests, rejectAccessRequest } from "@admin/services/adminApi";
import { Pill } from "@admin/components/Primitives";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", timeZone: "Asia/Kolkata" }) : "—");
const STATUS_COLOR = { pending: "gold", approved: "emerald", rejected: "rose" };

export function AccessRequestsPanel({ onApprove, refreshKey }) {
  const [data, setData] = useState({ items: [], counts: {}, can_decide: false });
  const [showDecided, setShowDecided] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    fetchAccessRequests(showDecided ? "all" : "pending").then(setData)
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load access requests"));
  }, [showDecided]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const reject = async (r) => {
    const reason = window.prompt(`Reason for rejecting ${r.email}'s admin access request:`);
    if (!reason || reason.trim().length < 3) return;
    setBusy(r.request_id);
    try {
      await rejectAccessRequest(r.request_id, reason.trim());
      toast.success("Request rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Reject failed"); }
    setBusy(null);
  };

  const pending = data.counts.pending || 0;
  return (
    <section className="mb-8" data-testid="access-requests-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base md:text-lg font-bold text-[#0A2540]" style={{ fontFamily: "Poppins" }}>
          Admin access requests <span className="ml-1 text-xs font-bold px-2 py-0.5 rounded-full bg-[#D4AF37]/20 text-[#0A2540]" data-testid="access-requests-pending-count">{pending} pending</span>
        </h2>
        <label className="text-xs text-slate-600 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showDecided} onChange={(e) => setShowDecided(e.target.checked)} data-testid="access-requests-show-decided" />
          Show decided
        </label>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="access-requests-table">
        {data.items.length === 0 ? (
          <div className="p-6 text-center text-sm text-slate-400" data-testid="access-requests-empty">No {showDecided ? "" : "pending "}access requests.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500">
              <tr>{["Candidate", "Reason", "Requested", "Status", "Actions"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3 text-xs uppercase tracking-wider">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r.request_id} className="border-t border-slate-100" data-testid={`access-request-row-${r.request_id}`}>
                  <td className="px-4 py-3"><p className="font-bold text-[#0A2540]">{r.name || r.email}</p><p className="text-[11px] text-slate-500" data-testid={`access-request-email-${r.request_id}`}>{r.email}</p></td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[320px]" data-testid={`access-request-reason-${r.request_id}`}>{r.reason}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap"><Clock size={11} className="inline mr-1" />{fmt(r.created_at)}</td>
                  <td className="px-4 py-3">
                    <span data-testid={`access-request-status-${r.request_id}`}><Pill color={STATUS_COLOR[r.status] || "slate"}>{r.status}</Pill></span>
                    {r.status !== "pending" && <p className="text-[10px] text-slate-400 mt-1">{fmt(r.decided_at)}{r.decision_reason ? ` · ${r.decision_reason}` : ""}</p>}
                  </td>
                  <td className="px-4 py-3">
                    {r.status === "pending" && !data.can_decide ? (
                      <span className="text-[11px] text-slate-400" data-testid={`access-request-super-admin-only-${r.request_id}`}>Super Admin only</span>
                    ) : r.status === "pending" ? (
                      <div className="flex gap-1">
                        <button onClick={() => onApprove(r)} className="px-2 py-1 text-xs font-bold rounded-md bg-[#0A2540] text-white hover:bg-[#123560]" data-testid={`approve-access-request-${r.request_id}`}><Check size={10} className="inline" /> Approve</button>
                        <button onClick={() => reject(r)} disabled={busy === r.request_id} className="px-2 py-1 text-xs font-bold rounded-md text-rose-700 border border-rose-200 hover:bg-rose-50 disabled:opacity-50" data-testid={`reject-access-request-${r.request_id}`}><X size={10} className="inline" /> Reject</button>
                      </div>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="mt-2 text-[11px] text-slate-400" data-testid="access-requests-note">Only a Super Admin can approve or reject, with fresh 2FA, and never their own request. Rejected requests never grant access; super_admin cannot be granted here.</p>
    </section>
  );
}
